import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Box, Tabs, Tab, Typography, CircularProgress, Button } from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { getData } from '../../api/dataSlice';
import { getDashboardRawData, dashboardFmtDate, dashboardParseDate, parseBotDate, translateDetectionType, databaseDateToFilename, loadDashboardData, isDashboardDataLoaded, getDashboardMinDate, getDashboardMaxDate } from '../Utilities';
import { setDashboardActions } from '../chatbot/ChatManager';
import { getAiParameterId, useGetAiParameterEnabledQuery } from '../../api/graphqlApi';
import { isMobile } from 'react-device-detect';
import dayjs from 'dayjs';
import DetectionFilters from './DetectionFilters';
import DetectionChart from './DetectionChart';
import WeatherFilters from './WeatherFilters';
import WeatherCharts from './WeatherCharts';
import DetectionDialog from './DetectionDialog';

const WEATHER_STREAMS = [
    { key: 'temp', color: '#e74c3c', i18nKey: 'pages.dashboard.temperature' },
    { key: 'precipitation', color: '#003366', i18nKey: 'pages.dashboard.precipitation' },
    { key: 'snowfall', color: '#95a5a6', i18nKey: 'pages.dashboard.snowfall' },
    { key: 'wind', color: '#27ae60', i18nKey: 'pages.dashboard.wind' },
    { key: 'humidity', color: '#8e44ad', i18nKey: 'pages.dashboard.humidity' },
]
const SUMMARY_SKIP_COLS = new Set(['image_filename', 'timestamp', 'archive_picture_id', 'num_persons', 'num_vehicles'])

function daysBetween(d1, d2) { return Math.round((d2 - d1) / 86400000) + 1 }

// format date to YYYY-MM-DDTHH:MM (extends dashboardFmtDate with time)
function fmtDateTime(date) {
    return dashboardFmtDate(date) + 'T' + String(date.getHours()).padStart(2, '0') + ':' + String(date.getMinutes()).padStart(2, '0')
}

function getDetections(startString, endString, archiveSource, cameraId) {

    const { summaryData } = getDashboardRawData(cameraId)
    if (!summaryData) return { error: 'Detection data not available' }

    const startDate = dashboardParseDate(startString)
    const endDate = dashboardParseDate(endString)
    const numDays = daysBetween(startDate, endDate)
    const isPerImage = numDays <= 1

    const filtered = summaryData.filter(elem => {
        const timestamp = new Date(elem.timestamp)
        const date = new Date(timestamp.getFullYear(), timestamp.getMonth(), timestamp.getDate())
        return date >= startDate && date <= endDate
    })

    const skip = SUMMARY_SKIP_COLS
    const parentCols = filtered.length ? Object.keys(summaryData[0]).filter(elem => !skip.has(elem) && !elem.includes('*')) : []
    const subtypeCols = filtered.length ? Object.keys(summaryData[0]).filter(elem => elem.includes('*')) : []

    if (isPerImage) {
        // build lookup: filename -> summaryData row
        const summaryByFile = {}
        for (const elem of filtered) {
            if (elem.image_filename) summaryByFile[elem.image_filename] = elem
        }

        // build complete timeline from archive source (includes images without detections)
        const timeline = []
        if (archiveSource?.[0]) {
            const endBound = new Date(endDate.getTime() + 86400000)
            for (const elem of archiveSource[0]) {
                if (elem[0] < startDate || elem[0] > endBound) continue
                const camera = elem[1]?.[cameraId]
                if (!camera?.datum) continue
                const filename = databaseDateToFilename(camera.datum)
                timeline.push({ dateStr: fmtDateTime(elem[0]), filename, row: summaryByFile[filename] || null })
            }
        }

        // fallback: if no archive source, use summaryData only
        if (!timeline.length && filtered.length) {
            for (const elem of filtered) {
                if (!elem.image_filename) continue
                timeline.push({ dateStr: fmtDateTime(new Date(elem.timestamp)), filename: elem.image_filename, row: elem })
            }
            timeline.sort((a, b) => a.dateStr.localeCompare(b.dateStr))
        }

        if (!timeline.length) return { error: 'No data for this range' }

        const dates = timeline.map(elem => elem.dateStr)
        const dailyCounts = {}

        dailyCounts['person'] = timeline.map(elem => elem.row ? (elem.row.num_persons || 0) : 0)

        for (const parentCol of parentCols) {
            dailyCounts[parentCol] = timeline.map(elem => {
                if (!elem.row) return 0
                let val = elem.row[parentCol] || 0
                for (const subtypeCol of subtypeCols) {
                    if (subtypeCol.split('*')[0] === parentCol) val -= (elem.row[subtypeCol] || 0)
                }
                return Math.max(0, val)
            })
        }

        for (const subtypeCol of subtypeCols) {
            dailyCounts[subtypeCol] = timeline.map(elem => elem.row ? (elem.row[subtypeCol] || 0) : 0)
        }

        const typeTotals = {}
        for (const elem in dailyCounts) typeTotals[elem] = dailyCounts[elem].reduce((a, b) => a + b, 0)

        const allTypes = Object.keys(dailyCounts).filter(elem => typeTotals[elem] > 0)
        allTypes.sort((a, b) => typeTotals[b] - typeTotals[a])

        return {
            dates,
            object_types: allTypes,
            daily_counts: Object.fromEntries(allTypes.map(elem => [elem, dailyCounts[elem]])),
            type_totals: Object.fromEntries(allTypes.map(elem => [elem, typeTotals[elem]])),
            image_filenames: timeline.map(elem => elem.filename),
            resolution: 'per_image'
        }
    }

    // daily resolution — generate ALL dates in range (including days without detections)
    if (!filtered.length) return { error: 'No detection data for this range' }

    const binned = filtered.map(elem => {
        const timestamp = new Date(elem.timestamp)
        return { ...elem, _bin: dashboardFmtDate(new Date(timestamp.getFullYear(), timestamp.getMonth(), timestamp.getDate())) }
    })

    // generate continuous date range (startDate .. endDate)
    const bins = []
    const cursor = new Date(startDate)
    while (cursor <= endDate) {
        bins.push(dashboardFmtDate(cursor))
        cursor.setDate(cursor.getDate() + 1)
    }

    const groups = {}
    for (const elem of bins) groups[elem] = []
    for (const elem of binned) {
        if (groups[elem._bin]) groups[elem._bin].push(elem)
    }

    const dailyCounts = {}

    // person counts → key is 'person'
    dailyCounts['person'] = bins.map(elem => (groups[elem] || []).reduce((a, b) => a + (b.num_persons || 0), 0))

    // parent types (subtract promoted subtypes) - key = API column name
    for (const parentCol of parentCols) {
        dailyCounts[parentCol] = bins.map(bin => {
            const rows = groups[bin] || []
            let parentVal = rows.reduce((a, b) => a + (b[parentCol] || 0), 0)
            for (const subtypeCol of subtypeCols) {
                if (subtypeCol.split('*')[0] === parentCol) {
                    parentVal -= rows.reduce((a, b) => a + (b[subtypeCol] || 0), 0)
                }
            }
            return Math.max(0, parentVal)
        })
    }

    // promoted subtypes → key is the composite API key
    for (const elem of subtypeCols) {
        dailyCounts[elem] = bins.map(bin => (groups[bin] || []).reduce((a, b) => a + (b[elem] || 0), 0))
    }

    const typeTotals = {}
    for (const elem in dailyCounts) typeTotals[elem] = dailyCounts[elem].reduce((a, b) => a + b, 0)
    const allTypes = Object.keys(dailyCounts).filter(elem => typeTotals[elem] > 0)
    allTypes.sort((a, b) => typeTotals[b] - typeTotals[a])

    // pick one representative image filename per bin (for tooltip preview)
    const imageFilenames = {}
    for (const bin of bins) {
        let best = groups[bin][0], bestDiff = Infinity
        for (const elem of groups[bin]) {
            const diff = Math.abs(new Date(elem.timestamp).getHours() - 12)
            if (diff < bestDiff) {
                bestDiff = diff;
                best = elem
            }
        }
        imageFilenames[bin] = best?.image_filename || null
    }

    return {
        dates: bins,
        object_types: allTypes,
        daily_counts: Object.fromEntries(allTypes.map(elem => [elem, dailyCounts[elem]])),
        type_totals: Object.fromEntries(allTypes.map(elem => [elem, typeTotals[elem]])),
        image_filenames: bins.map(elem => imageFilenames[elem] || null),
        resolution: 'daily'
    }
}

function getWeather(startString, endString, cameraId) {

    const { weatherHourly, weatherDaily } = getDashboardRawData(cameraId)

    const startDate = dashboardParseDate(startString)
    const endDate = dashboardParseDate(endString)
    const numDays = daysBetween(startDate, endDate)

    if (numDays <= 14) {

        if (!weatherHourly) return { resolution: 'hourly', dates: [] }

        const filtered = weatherHourly.filter(elem => {
            const date = new Date(elem.timestamp)
            return date >= startDate && date < new Date(endDate.getTime() + 86400000)
        })

        return {
            resolution: 'hourly',
            dates: filtered.map(elem => {
                const date = new Date(elem.timestamp)
                return dashboardFmtDate(date) + ' ' + String(date.getHours()).padStart(2, '0') + ':' + String(date.getMinutes()).padStart(2, '0')
            }),
            temp: filtered.map(elem => elem.temp || 0),
            precipitation: filtered.map(elem => elem.precipitation || 0),
            snowfall: filtered.map(elem => elem.snow || 0),
            wind_speed: filtered.map(elem => elem.windSpeed || 0),
            wind_gusts: filtered.map(elem => elem.windGusts || 0),
            humidity: filtered.map(elem => elem.humidity || 0),
            weather_description: filtered.map(elem => elem.weather || ''),
        }

    } else {

        if (!weatherDaily) return { resolution: 'daily', dates: [] }

        const filtered = weatherDaily.filter(elem => {
            const date = new Date(elem.date)
            return date >= startDate && date <= endDate
        })

        return {
            resolution: 'daily',
            dates: filtered.map(elem => dashboardFmtDate(new Date(elem.date))),
            temp_max: filtered.map(elem => elem.temp_max || 0),
            temp_min: filtered.map(elem => elem.temp_min || 0),
            temp_avg: filtered.map(elem => elem.temp_avg || 0),
            precipitation: filtered.map(elem => elem.precipitation_sum || 0),
            snowfall: filtered.map(elem => elem.snow_sum || 0),
            wind_max: filtered.map(elem => elem.windSpeed_max || 0),
            wind_avg: filtered.map(elem => elem.windSpeed_avg || 0),
            wind_gusts: filtered.map(elem => elem.windGusts_max || 0),
            humidity: filtered.map(elem => elem.humidity_avg || 0),
            weather_description: filtered.map(elem => elem.weather || '')
        }
    }
}

export default function DashboardContent({ dashboardState, onDashboardStateChange, contentHeight, contentWidth, pendingViewer, onPendingViewerConsumed }) {

    const { t } = useTranslation()
    const { CAMERAS, BASEURL } = window.conf
    const { source } = useSelector(getData)

    const [activeTab, setActiveTab] = useState(dashboardState?.activeTab ?? 'detections')
    const [detectionData, setDetectionData] = useState(null)
    const [weatherData, setWeatherData] = useState(null)
    const [enabledTypes, setEnabledTypes] = useState(new Set())
    const [weatherStreams, setWeatherStreams] = useState(new Set())
    const [viewerDate, setViewerDate] = useState(null)
    const [viewerTimestamp, setViewerTimestamp] = useState(null)
    const [viewerInitialType, setViewerInitialType] = useState(null)
    const [loading, setLoading] = useState(false)
    const [filtersHeight, setFiltersHeight] = useState(0)

    const observerRef = useRef(null)
    const stateRef = useRef({})
    const pendingTypesRef = useRef(undefined)
    const allKnownTypesRef = useRef([]) // mirror of `allKnownTypes` so chatbot actions read a stable catalog
    const tType = useCallback((key) => translateDetectionType(key, t), [t])

    const cameraId = dashboardState?.selectedCamera ?? CAMERAS?.[0]?.id ?? 0
    const baseUrl = `${BASEURL}cam${cameraId + 1}/zeitraffer/`
    const chartOverhead = isMobile ? 150 : 200 // tabs + padding + margins
    const chartHeight = contentHeight ? Math.max(250, contentHeight - filtersHeight - chartOverhead) : 500
    const sortedWeatherStreams = useMemo(() => [...WEATHER_STREAMS].sort((a, b) => t(a.i18nKey).localeCompare(t(b.i18nKey))), [t]) // sort localised alphabetically; shared between chips and charts so both render in identical order

    const { data: reportAiParameter } = useGetAiParameterEnabledQuery({ aiParameterId: getAiParameterId('AI_REPORT_ENABLED') })
    const hasDetections = reportAiParameter?.enabled
    const hasDetectionsRef = useRef(hasDetections)
    const canGoBack = !!dashboardState?.canGoBack // "Back to overview" button shows only while we came from a multi-day overview (drill-down)

    // collapse to weather-only when the detections tab disappears
    useEffect(() => {
        hasDetectionsRef.current = hasDetections
        if (hasDetections === false && activeTab === 'detections') {
            setActiveTab('weather')
        }
    }, [hasDetections, activeTab])

    // mirror activeTab into shared dashboardState so DashboardAccordion can pick the right calendar bounds
    useEffect(() => {
        onDashboardStateChange?.(prev => prev && prev.activeTab !== activeTab ? { ...prev, activeTab } : prev)
    }, [activeTab, onDashboardStateChange])

    // expose dashboardActions for the chatbot (stable object, reads from ref)
    useEffect(() => {
        if (!dashboardState?.dataReady) return
        const actions = {
            getActiveTab: () => stateRef.current.activeTab,
            getEnabledDetectionTypes: () => {
                const types = stateRef.current.enabledTypes
                return types ? [...types].map(elem => stateRef.current.tType(elem)) : []
            },
            getDetectionTypes: () => {
                const data = stateRef.current.detectionData
                return data?.object_types ? data.object_types.map(elem => stateRef.current.tType(elem)) : []
            },
            getDateRange: () => {
                const dateRange = dashboardState?.dateRange
                if (!dateRange?.[0] || !dateRange?.[1]) return null
                const fmt = d => d.format('YYYY-MM-DD')
                return { start: fmt(dateRange[0]), end: fmt(dateRange[1]) }
            },
            getWeatherStreams: () => {
                const streams = stateRef.current.weatherStreams
                return streams ? [...streams] : []
            },
            getDataLoaded: () => stateRef.current.detectionData !== null,
            getViewerDate: () => stateRef.current.viewerDate || null,
            getSelectedCameraId: () => stateRef.current.cameraId ?? 0,
            setSelectedCameraId: (id) => {
                if (id == null) return
                dashboardState?.onSetSelectedCamera?.(id) // routed through DashboardAccordion (camera state lives there); validation effect re-checks against the AI-enabled set
            },
            showOnlyDetections: (deNames) => {
                // reverse-translate German names against the stable catalog (not detectionData, which can be stale during a drill-down before the new range has loaded)
                const allTypes = allKnownTypesRef.current || []
                const matched = new Set()
                for (const deName of deNames) {
                    for (const key of allTypes) {
                        if (stateRef.current.tType(key) === deName) { matched.add(key); break }
                    }
                }
                setEnabledTypes(matched) // always set — even empty — so stale filters don't leak
            },
            showAllDetections: () => {
                const data = stateRef.current.detectionData
                if (data) setEnabledTypes(new Set(data.object_types))
            },
            switchTab: (tab) => {
                if (tab === 'detections' && !hasDetectionsRef.current) return // detections tab hidden by AI-parameter gate
                setActiveTab(tab)
            },
            showWeatherStreams: (streams) => {
                setWeatherStreams(new Set(streams))
                setActiveTab('weather')
            },
            setDates: (start, end, typesOverride) => {
                const startIso = parseBotDate(start)?.format('YYYY-MM-DD') || null
                const endIso = parseBotDate(end)?.format('YYYY-MM-DD') || null

                // store pending types so the data reload effect can apply them after data arrives
                if (typesOverride !== undefined) {
                    pendingTypesRef.current = typesOverride
                }

                if (startIso && endIso && dashboardState?.onSetDateRange) {
                    dashboardState.onSetDateRange(startIso, endIso)
                }

                // apply types directly if no date change triggers a reload
                if (!startIso || !endIso) {
                    if (typesOverride && Array.isArray(typesOverride)) {
                        actions.showOnlyDetections(typesOverride)
                    } else if (typesOverride === null) {
                        actions.showAllDetections()
                    }
                    pendingTypesRef.current = undefined
                }
            },
            resetView: () => {
                setActiveTab(hasDetectionsRef.current ? 'detections' : 'weather')
                setWeatherStreams(new Set(stateRef.current.activeWeatherStreams || [])) // default = streams that have data in the current scope (matches the activeWeatherStreams seed effect)
                const data = stateRef.current.detectionData
                if (data) setEnabledTypes(new Set(data.object_types))
            },
            openViewer: (date, timestamp, deTypeNames) => {
                if (!date) return
                if (!hasDetectionsRef.current) return // gated by AI parameter
                if (deTypeNames?.length) pendingTypesRef.current = deTypeNames // stash type filter for the data-reload effect to apply atomically — applying it directly here races with the drill-down's data reload
                setActiveTab('detections')
                if (dashboardState?.onDrillDown) dashboardState.onDrillDown(date)
                setViewerDate(dashboardFmtDate(dashboardParseDate(date))) // normalize to YYYY-MM-DD
                setViewerTimestamp(timestamp || null)
            },
        }
        setDashboardActions(actions)
        return () => setDashboardActions(null)
    // the `actions` closure reads live state via refs (stateRef/hasDetectionsRef), so we don't depend on every setter/state slice here — only on the inputs that should cause a re-registration (dataReady / dateRange)
    }, [dashboardState?.dataReady, dashboardState?.dateRange]) // eslint-disable-line react-hooks/exhaustive-deps

    // consume a pending "open detection viewer" request from the archive's "to object detections and weather" button; self-loads the RAG data and seeds dashboardState
    useEffect(() => {
        if (!pendingViewer) return
        if (hasDetections === undefined) return // wait for the AI-detections gate before deciding tab/viewer

        let cancelled = false
        const run = async () => {
            if (!isDashboardDataLoaded(cameraId)) {
                await loadDashboardData(`${BASEURL}cam${cameraId + 1}/rag_data/`, cameraId)
            }
            if (cancelled) return

            const day = dayjs(pendingViewer.date)

            // bootstrap dashboardState so the body renders even with the accordion unmounted; preserve handlers the accordion may have published
            onDashboardStateChange?.(prev => {
                const min = getDashboardMinDate(cameraId, 'detections')
                const max = getDashboardMaxDate(cameraId, 'detections')
                return {
                    ...(prev || {}),
                    selectedCamera: prev?.selectedCamera ?? cameraId,
                    dateRange: [day, day],
                    dataReady: true,
                    minDate: prev?.minDate ?? (min ? dayjs(min) : null),
                    maxDate: prev?.maxDate ?? (max ? dayjs(max) : null),
                    canGoBack: false,
                    previousRange: null
                }
            })

            if (pendingViewer.openViewer && hasDetections) {
                setActiveTab('detections')
                setViewerInitialType(null)
                setViewerDate(pendingViewer.date) // already YYYY-MM-DD
                setViewerTimestamp(pendingViewer.timestamp || null)
            }

            onPendingViewerConsumed?.()
        }
        run()
        return () => { cancelled = true }
    // fires when the request arrives or the detections gate resolves; setters/props are stable enough to omit
    }, [pendingViewer, hasDetections]) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!dashboardState?.dataReady || !dashboardState?.dateRange) return
        const [start, end] = dashboardState.dateRange
        if (!start || !end) return

        setLoading(true)

        const startString = dashboardFmtDate(start.toDate())
        const endString = dashboardFmtDate(end.toDate())
        const detections = getDetections(startString, endString, source, cameraId)

        if (!detections.error) {
            setDetectionData(detections)

            const pending = pendingTypesRef.current
            pendingTypesRef.current = undefined

            if (pending !== undefined) {
                // chatbot requested a type filter alongside the date change — apply it
                if (Array.isArray(pending)) {
                    const matched = new Set()
                    for (const deName of pending) {
                        for (const key of detections.object_types) {
                            if (tType(key) === deName) { matched.add(key); break }
                        }
                    }
                    setEnabledTypes(matched.size > 0 ? matched : new Set(detections.object_types))
                } else if (pending === null) {
                    setEnabledTypes(new Set(detections.object_types))
                }
            } else {
                setEnabledTypes(prev => {
                    // single-day view (drill-down) - keep selected filters intersected with available types
                    if (prev.size > 0) {
                        const intersection = new Set(detections.object_types.filter(elem => prev.has(elem)))
                        return intersection.size > 0 ? intersection : new Set(detections.object_types)
                    }
                    return new Set(detections.object_types)
                })
            }

        } else {
            setDetectionData(null)
            setEnabledTypes(new Set())
        }

        setWeatherData(getWeather(startString, endString, cameraId))
        setLoading(false)
    // source/tType intentionally excluded: `source` updates happen as images load and would trigger redundant
    // recomputations of the same day range; `tType` is a stable callback. Effect fires only on the three real triggers.
    }, [dashboardState?.dataReady, dashboardState?.dateRange, dashboardState?.selectedCamera]) // eslint-disable-line react-hooks/exhaustive-deps

    // compute ALL known types from the full dataset (per camera), so we can show inactive types grayed out
    const allKnownTypes = useMemo(() => {
        const { summaryData } = getDashboardRawData(cameraId)
        if (!summaryData?.length) return []
        const skip = SUMMARY_SKIP_COLS
        const types = new Set()
        types.add('person')
        for (const elem of Object.keys(summaryData[0])) {
            if (!skip.has(elem)) types.add(elem)
        }
        return [...types]
    // getDashboardRawData / SUMMARY_SKIP_COLS are stable; only cameraId + dataReady drive a recompute
    }, [dashboardState?.dataReady, cameraId]) // eslint-disable-line react-hooks/exhaustive-deps

    allKnownTypesRef.current = allKnownTypes // mirror into a ref so chatbot actions can read the stable catalog regardless of data-load timing

    // callback ref: attach ResizeObserver whenever the filters DOM element mounts (incl. after tab switch)
    const filtersRef = useCallback((node) => {
        if (observerRef.current) {
            observerRef.current.disconnect();
            observerRef.current = null
        }
        if (node) {
            const observer = new ResizeObserver(elem => setFiltersHeight(elem[0].contentRect.height))
            observer.observe(node)
            observerRef.current = observer
        } else {
            setFiltersHeight(0)
        }
    }, [])

    const toggleType = useCallback((type) => {
        setEnabledTypes(prev => {
            const next = new Set(prev)
            next.has(type) ? next.delete(type) : next.add(type)
            return next
        })
    }, [])

    const activeWeatherStreams = useMemo(() => {
        if (!weatherData || !weatherData.dates?.length) return new Set()
        const hasAny = (arr) => arr?.some(v => v !== 0 && v !== '')
        const isHourly = weatherData.resolution === 'hourly'
        const active = new Set()
        if (hasAny(isHourly ? weatherData.temp : weatherData.temp_max)) active.add('temp')
        if (hasAny(weatherData.precipitation)) active.add('precipitation')
        if (hasAny(weatherData.snowfall)) active.add('snowfall')
        if (hasAny(isHourly ? weatherData.wind_speed : weatherData.wind_max)) active.add('wind')
        if (hasAny(weatherData.humidity)) active.add('humidity')
        return active
    }, [weatherData])

    // seed `weatherStreams` (user-on set) from streams that actually have data — runs on every data scope change
    useEffect(() => {
        setWeatherStreams(new Set(activeWeatherStreams))
    }, [activeWeatherStreams])

    // keep ref in sync for chatbot actions (placed AFTER activeWeatherStreams declaration to avoid a TDZ in the deps array — the array is evaluated during render, so all referenced consts must already be declared)
    useEffect(() => {
        stateRef.current = { activeTab, enabledTypes, weatherStreams, detectionData, viewerDate, cameraId, tType, activeWeatherStreams }
    }, [activeTab, enabledTypes, weatherStreams, detectionData, viewerDate, cameraId, tType, activeWeatherStreams])

    const toggleWeatherStream = useCallback((key) => {
        setWeatherStreams(prev => {
            const next = new Set(prev)
            next.has(key) ? next.delete(key) : next.add(key)
            return next
        })
    }, [])

    const handleDayZoom = useCallback((dateString) => {
        dashboardState?.onDrillDown?.(dateString)
    // only the onDrillDown reference matters — `dashboardState` itself is irrelevant for this callback's identity
    }, [dashboardState?.onDrillDown]) // eslint-disable-line react-hooks/exhaustive-deps

    const handleBarClick = useCallback((dateString, timestamp, clickedType) => {
        setViewerDate(dateString)
        setViewerTimestamp(timestamp || null)
        setViewerInitialType(clickedType || null)
    }, [])

    const handleViewerClose = useCallback(() => {
        setViewerDate(null)
        setViewerTimestamp(null)
        setViewerInitialType(null)
    }, [])

    return (
        <Box sx={{
            display: 'block',
            width: contentWidth,
            height: contentHeight,
            overflow: 'auto',
            background: '#f5f6fa',
            transition: '0.2s linear'
        }}>
            {!dashboardState?.dataReady ? (
                <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100%'
                }}>
                    <CircularProgress sx={{ mb: 2 }} />
                    <Typography color="text.secondary">{t('pages.dashboard.loading_data')}</Typography>
                </Box>
            ) : (
                <Box sx={{ p: isMobile ? 1 : 2, height: '100%', overflowY: 'auto', overflowX: 'hidden', boxSizing: 'border-box' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: isMobile ? 1 : 2, flexWrap: 'nowrap' }}>
                        {canGoBack && (
                            <Button
                                onClick={() => dashboardState?.onDrillUp?.()}
                                startIcon={<ArrowBack />}
                                size={isMobile ? 'small' : 'medium'}
                                sx={{ textTransform: 'none', color: '#003366', flexShrink: 0, minWidth: 'auto', whiteSpace: 'nowrap' }}
                            >
                                {t('actions.overview')}
                            </Button>
                        )}
                        <Tabs
                            value={activeTab}
                            onChange={(_, value) => setActiveTab(value)}
                            sx={{
                                flexGrow: 1, flexShrink: 1, minWidth: 0,
                                bgcolor: 'white', borderRadius: 1, boxShadow: '0 2px 4px rgba(0,0,0,0.08)',
                                minHeight: isMobile ? 36 : 48,
                                '& .MuiTab-root': { fontWeight: 600, fontSize: 14, textTransform: 'none', minHeight: isMobile ? 36 : 48, minWidth: 0, maxWidth: 'none', px: 1.5 },
                                '& .Mui-selected': { color: '#003366' },
                                '& .MuiTabs-indicator': { backgroundColor: '#003366', height: 3 }
                            }}
                        >
                            <Tab
                                value="detections"
                                disabled={!hasDetections}
                                label={<Box component="span" sx={{ display: 'block', width: '100%', textAlign: 'center', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t('pages.dashboard.tab_detections')}</Box>}
                            />
                            <Tab
                                value="weather"
                                label={<Box component="span" sx={{ display: 'block', width: '100%', textAlign: 'center', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t('pages.dashboard.tab_weather')}</Box>}
                            />
                        </Tabs>
                    </Box>

                    {activeTab === 'detections' && hasDetections && (
                        <Box>
                            {loading ? (
                                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                                    <CircularProgress />
                                </Box>
                            ) :
                                detectionData && detectionData.object_types.length > 0 ? (
                                    <>
                                        <Box ref={filtersRef}>
                                            <DetectionFilters
                                                objectTypes={allKnownTypes}
                                                activeTypes={detectionData.object_types}
                                                enabledTypes={enabledTypes}
                                                typeTotals={detectionData.type_totals}
                                                onToggle={toggleType}
                                                onSolo={(type) => setEnabledTypes(new Set([type]))}
                                                onAll={() => setEnabledTypes(new Set(detectionData.object_types))}
                                                onNone={() => setEnabledTypes(new Set())}
                                                translateType={tType}
                                                totalDetections={Object.values(detectionData.type_totals).reduce((a, b) => a + b, 0)}
                                                imageCount={detectionData.dates?.length || 0}
                                            />
                                        </Box>
                                        <DetectionChart
                                            data={detectionData}
                                            enabledTypes={enabledTypes}
                                            onDayZoom={handleDayZoom}
                                            onBarClick={handleBarClick}
                                            baseUrl={baseUrl}
                                            translateType={tType}
                                            chartHeight={chartHeight}
                                        />
                                    </>
                                ) : (
                                    <Typography color="text.secondary" sx={{ textAlign: 'center', p: 4 }}>
                                        {t('pages.dashboard.no_data')}
                                    </Typography>
                                )
                            }
                        </Box>
                    )}

                    {activeTab === 'weather' && (
                        <Box>
                            <WeatherFilters
                                streamDefs={sortedWeatherStreams}
                                streams={weatherStreams}
                                onToggle={toggleWeatherStream}
                                onSolo={(key) => setWeatherStreams(new Set([key]))}
                            />

                            {weatherData ? (
                                <WeatherCharts
                                    weatherData={weatherData}
                                    streams={weatherStreams}
                                    streamDefs={sortedWeatherStreams}
                                />
                            ) : (
                                <Typography color="text.secondary" sx={{ textAlign: 'center', p: 4 }}>
                                    {t('pages.dashboard.no_data')}
                                </Typography>
                            )}
                        </Box>
                    )}

                    {viewerDate && (
                        <DetectionDialog
                            key={`${viewerDate}-${viewerTimestamp || 'none'}-${viewerInitialType || 'none'}`} // remount on new timestamp/type so DetectionDialog's init effect re-runs
                            date={viewerDate}
                            onClose={handleViewerClose}
                            initialEnabledTypes={enabledTypes}
                            initialTimestamp={viewerTimestamp}
                            initialClickedType={viewerInitialType}
                            baseUrl={baseUrl}
                            translateType={tType}
                            cameraId={cameraId}
                        />
                    )}
                </Box>
            )}
        </Box>
    )
}
